import { useEffect, useMemo, useRef, useState } from 'react';
import { Mail, RotateCcw, Send } from 'lucide-react';
import { adminMailApi } from '../api/adminMailApi';
import { apiErrorMessage } from '../api/errors';
import { Select, TextField } from '../components/forms';
import { Btn, SectionHeading } from '../components/ui';
import { newId } from '../lib/ids';
import { c } from '../styles/theme';
import type {
  AdminMailDeliveryProgress,
  AdminMailDraft,
  BonusAudienceFilter,
  BonusPurchasePlatform,
} from '../types/domain';

const FIELD_STYLE = { padding: '8px 10px' } as const;
const MAX_SPECIFIC_EMAILS = 500;
const PURCHASE_PLATFORMS: { value: BonusPurchasePlatform; label: string }[] = [
  { value: 'jvzoo', label: 'JVZoo' },
  { value: 'launchpadjv', label: 'Launchpad JV' },
  { value: 'gumroad', label: 'Gumroad' },
  { value: 'admin', label: 'Admin' },
];

const DEFAULT_DRAFT: AdminMailDraft = {
  subject: '',
  heading: '',
  message: '',
  audience: { levels: [], verification: 'any', purchasePlatforms: [] },
};

function parseSpecificEmails(value: string): { emails?: string[]; error?: string } {
  const emails = [
    ...new Set(
      value
        .split(',')
        .map((email) => email.trim().toLowerCase())
        .filter(Boolean)
    ),
  ];
  if (emails.length === 0) return { error: 'Enter at least one user email address.' };
  if (emails.length > MAX_SPECIFIC_EMAILS) {
    return { error: `Enter no more than ${MAX_SPECIFIC_EMAILS} email addresses.` };
  }
  if (emails.some((email) => email.length > 254 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))) {
    return { error: 'Enter valid email addresses separated by commas.' };
  }
  return { emails };
}

export default function AdminMail() {
  const [draft, setDraft] = useState<AdminMailDraft>(DEFAULT_DRAFT);
  const [audienceMode, setAudienceMode] = useState<'filters' | 'emails'>('filters');
  const [audienceEmails, setAudienceEmails] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewFingerprint, setPreviewFingerprint] = useState('');
  const [recipientCount, setRecipientCount] = useState(0);
  const [delivery, setDelivery] = useState<AdminMailDeliveryProgress | null>(null);
  const [dispatchFingerprint, setDispatchFingerprint] = useState('');
  const [pending, setPending] = useState<'preview' | 'send' | null>(null);
  const [error, setError] = useState('');
  const fingerprint = useMemo(
    () => JSON.stringify([draft, audienceMode, audienceEmails]),
    [draft, audienceMode, audienceEmails]
  );
  const fingerprintRef = useRef(fingerprint);
  fingerprintRef.current = fingerprint;
  const hasCurrentPreview = previewHtml.length > 0 && previewFingerprint === fingerprint;
  const deliveryActive = Boolean(delivery && delivery.status !== 'completed');
  const result = delivery?.status === 'completed' ? delivery : null;

  useEffect(() => {
    const dispatchId = delivery?.id;
    if (!dispatchId || delivery.status === 'completed') return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const poll = async () => {
      try {
        const next = await adminMailApi.getProgress(dispatchId);
        if (cancelled) return;
        setDelivery(next);
        if (next.status !== 'completed') timer = setTimeout(() => void poll(), 400);
      } catch (err) {
        if (cancelled) return;
        setError(apiErrorMessage(err));
        timer = setTimeout(() => void poll(), 1_000);
      }
    };
    timer = setTimeout(() => void poll(), 250);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [delivery?.id, delivery?.status]);

  function clearCompletedDelivery() {
    setDelivery((current) => (current?.status === 'completed' ? null : current));
  }

  function patch(next: Partial<AdminMailDraft>) {
    setDraft((current) => ({ ...current, ...next }));
    setError('');
    clearCompletedDelivery();
  }

  function patchAudience(next: Partial<BonusAudienceFilter>) {
    setDraft((current) => ({
      ...current,
      audience: { ...current.audience, ...next },
    }));
    setError('');
    clearCompletedDelivery();
  }

  function preparedDraft(): AdminMailDraft | null {
    const subject = draft.subject.trim();
    const heading = draft.heading.trim();
    const message = draft.message.trim();
    if (!subject || !heading || !message) {
      setError('Complete the subject, heading, and message first.');
      return null;
    }
    if (draft.audience.joinedFrom && draft.audience.joinedTo) {
      if (draft.audience.joinedFrom > draft.audience.joinedTo) {
        setError('The joined-from date must be before the joined-to date.');
        return null;
      }
    }
    let audience = draft.audience;
    if (audienceMode === 'emails') {
      const parsed = parseSpecificEmails(audienceEmails);
      if (!parsed.emails) {
        setError(parsed.error ?? 'Enter at least one user email address.');
        return null;
      }
      audience = { ...audience, specificEmails: parsed.emails };
    } else {
      const { specificEmails: _specificEmails, ...filters } = audience;
      audience = filters;
    }
    return { subject, heading, message, audience };
  }

  async function handlePreview() {
    const prepared = preparedDraft();
    if (!prepared || pending) return;
    const requestedFingerprint = fingerprint;
    setPending('preview');
    setError('');
    try {
      const preview = await adminMailApi.preview(prepared);
      if (fingerprintRef.current !== requestedFingerprint) return;
      setPreviewHtml(preview.html);
      setRecipientCount(preview.recipientCount);
      setPreviewFingerprint(requestedFingerprint);
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setPending(null);
    }
  }

  async function handleSend() {
    const prepared = preparedDraft();
    if (!prepared || pending || deliveryActive) return;
    if (!hasCurrentPreview) {
      setError('Preview the current email and audience before sending.');
      return;
    }
    setPending('send');
    setError('');
    try {
      const dispatchId = newId();
      setDelivery(await adminMailApi.send(prepared, dispatchId));
      setDispatchFingerprint(fingerprint);
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setPending(null);
    }
  }

  function targetFailedEmails() {
    if (!result?.failedEmails.length) return;
    setAudienceMode('emails');
    setAudienceEmails(result.failedEmails.join(', '));
    setDraft((current) => ({
      ...current,
      audience: { ...current.audience, specificEmails: result.failedEmails },
    }));
    setDelivery(null);
    setDispatchFingerprint('');
    setError('');
    setPreviewHtml('');
    setPreviewFingerprint('');
  }

  return (
    <div>
      <SectionHeading
        as="h2"
        title="Mail"
        subtitle="Send an email to a selected account audience"
        marginBottom={14}
      />

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 360px), 1fr))',
          gap: 14,
          alignItems: 'start',
        }}
      >
        <section
          aria-label="Compose admin email"
          style={{
            border: `1px solid ${c.border}`,
            borderRadius: 8,
            padding: 16,
            background: c.surface1,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <Mail size={15} color={c.accent} />
            <h3 style={{ fontSize: 13, margin: 0 }}>Email content</h3>
          </div>
          <TextField
            label="Subject"
            value={draft.subject}
            onChange={(subject) => patch({ subject })}
            style={FIELD_STYLE}
            marginBottom={10}
          />
          <TextField
            label="Heading"
            value={draft.heading}
            onChange={(heading) => patch({ heading })}
            style={FIELD_STYLE}
            marginBottom={10}
          />
          <TextField
            label="Message"
            value={draft.message}
            onChange={(message) => patch({ message })}
            multiline
            rows={9}
            style={FIELD_STYLE}
          />
        </section>

        <section
          aria-label="Email audience"
          style={{
            border: `1px solid ${c.border}`,
            borderRadius: 8,
            padding: 16,
            background: c.surface1,
          }}
        >
          <h3 style={{ fontSize: 13, margin: '0 0 14px' }}>Audience</h3>
          <Select
            label="Audience selection"
            value={audienceMode}
            onChange={(value) => {
              setAudienceMode(value as 'filters' | 'emails');
              setError('');
              clearCompletedDelivery();
            }}
            options={[
              { value: 'filters', label: 'Use filters' },
              { value: 'emails', label: 'Specific emails' },
            ]}
            style={FIELD_STYLE}
            marginBottom={12}
          />

          {audienceMode === 'emails' ? (
            <TextField
              label="Specific user emails (comma-separated)"
              value={audienceEmails}
              onChange={(value) => {
                setAudienceEmails(value);
                setError('');
                clearCompletedDelivery();
              }}
              multiline
              rows={7}
              style={FIELD_STYLE}
            />
          ) : (
            <>
              <Select
                label="Verification status"
                value={draft.audience.verification}
                onChange={(verification) =>
                  patchAudience({
                    verification: verification as BonusAudienceFilter['verification'],
                  })
                }
                options={[
                  { value: 'any', label: 'Any status' },
                  { value: 'verified', label: 'Verified only' },
                  { value: 'unverified', label: 'Unverified only' },
                ]}
                style={FIELD_STYLE}
                marginBottom={12}
              />

              <fieldset style={{ border: 0, padding: 0, margin: '0 0 12px' }}>
                <legend style={{ fontSize: 12, color: c.text2, marginBottom: 6 }}>
                  Level status (none means any)
                </legend>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 14px' }}>
                  {Array.from({ length: 9 }, (_, level) => (
                    <label key={level} style={{ fontSize: 12, color: c.text1 }}>
                      <input
                        type="checkbox"
                        checked={draft.audience.levels.includes(level)}
                        onChange={(event) =>
                          patchAudience({
                            levels: event.target.checked
                              ? [...draft.audience.levels, level]
                              : draft.audience.levels.filter((candidate) => candidate !== level),
                          })
                        }
                      />{' '}
                      Level {level}
                    </label>
                  ))}
                </div>
              </fieldset>

              <fieldset style={{ border: 0, padding: 0, margin: '0 0 12px' }}>
                <legend style={{ fontSize: 12, color: c.text2, marginBottom: 6 }}>
                  Purchase platform (none means any)
                </legend>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 14px' }}>
                  {PURCHASE_PLATFORMS.map((platform) => (
                    <label key={platform.value} style={{ fontSize: 12, color: c.text1 }}>
                      <input
                        type="checkbox"
                        checked={draft.audience.purchasePlatforms.includes(platform.value)}
                        onChange={(event) =>
                          patchAudience({
                            purchasePlatforms: event.target.checked
                              ? [...draft.audience.purchasePlatforms, platform.value]
                              : draft.audience.purchasePlatforms.filter(
                                  (candidate) => candidate !== platform.value
                                ),
                          })
                        }
                      />{' '}
                      {platform.label}
                    </label>
                  ))}
                </div>
              </fieldset>

              <div className="lyx-grid-2">
                <TextField
                  label="Joined from"
                  type="date"
                  value={draft.audience.joinedFrom ?? ''}
                  onChange={(joinedFrom) => patchAudience({ joinedFrom })}
                  style={FIELD_STYLE}
                />
                <TextField
                  label="Joined to"
                  type="date"
                  value={draft.audience.joinedTo ?? ''}
                  onChange={(joinedTo) => patchAudience({ joinedTo })}
                  style={FIELD_STYLE}
                />
              </div>
            </>
          )}
        </section>
      </div>

      {error && (
        <p role="alert" style={{ color: c.danger, fontSize: 12, margin: '14px 0 0' }}>
          {error}
        </p>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 14 }}>
        <Btn
          variant="secondary"
          onClick={() => void handlePreview()}
          disabled={pending !== null || deliveryActive}
        >
          <Mail size={13} />
          {pending === 'preview' ? 'Loading preview…' : 'Preview email'}
        </Btn>
        <Btn
          onClick={() => void handleSend()}
          disabled={
            pending !== null ||
            !hasCurrentPreview ||
            recipientCount === 0 ||
            deliveryActive ||
            (delivery?.status === 'completed' && dispatchFingerprint === fingerprint)
          }
        >
          <Send size={13} />
          {deliveryActive
            ? 'Sending…'
            : result && dispatchFingerprint === fingerprint
              ? 'Email sent'
              : pending === 'send'
                ? 'Sending…'
                : hasCurrentPreview
                  ? `Send to ${recipientCount.toLocaleString('en-US')}`
                  : 'Send email'}
        </Btn>
      </div>

      {hasCurrentPreview && (
        <section aria-label="Email preview" style={{ marginTop: 16 }}>
          <p style={{ color: c.text2, fontSize: 12, margin: '0 0 8px' }}>
            {recipientCount.toLocaleString('en-US')}{' '}
            {recipientCount === 1 ? 'recipient' : 'recipients'}
          </p>
          <iframe
            title="Admin email preview"
            srcDoc={previewHtml}
            style={{
              display: 'block',
              width: '100%',
              height: 480,
              border: `1px solid ${c.border}`,
              borderRadius: 8,
              background: '#fff',
            }}
          />
        </section>
      )}

      {deliveryActive && delivery && (
        <section
          aria-label="Email delivery progress"
          style={{
            marginTop: 16,
            border: `1px solid ${c.accentBorder}`,
            borderRadius: 8,
            padding: 14,
            background: c.accentSoft,
          }}
        >
          <p style={{ color: c.text1, fontSize: 13, fontWeight: 600, margin: '0 0 8px' }}>
            {delivery.sentCount.toLocaleString('en-US')}/
            {delivery.recipientCount.toLocaleString('en-US')} successfully sent
          </p>
          <progress
            aria-label="Email delivery progress"
            value={delivery.sentCount}
            max={delivery.recipientCount}
            style={{ width: '100%', height: 8 }}
          />
          <p style={{ color: c.text2, fontSize: 11.5, margin: '8px 0 0' }}>
            {delivery.status === 'retrying'
              ? 'Retrying failed recipients once'
              : `${delivery.processedCount.toLocaleString('en-US')} checked`}
          </p>
        </section>
      )}

      {result && (
        <section
          aria-label="Email delivery result"
          style={{
            marginTop: 16,
            border: `1px solid ${result.failedCount ? c.accentBorder : c.border}`,
            borderRadius: 8,
            padding: 14,
            background: result.failedCount ? c.accentSoft : c.successSoft,
          }}
        >
          <p style={{ color: c.text1, fontSize: 13, fontWeight: 600, margin: 0 }}>
            {result.sentCount.toLocaleString('en-US')} sent ·{' '}
            {result.failedCount.toLocaleString('en-US')} failed
          </p>
          {result.failedEmails.length > 0 && (
            <>
              <p style={{ color: c.text2, fontSize: 12, margin: '10px 0 6px' }}>
                Failed recipients
              </p>
              <p style={{ color: c.text1, fontSize: 12, margin: '0 0 10px' }}>
                {result.failedEmails.join(', ')}
              </p>
              <Btn variant="secondary" onClick={targetFailedEmails}>
                <RotateCcw size={13} />
                Target failed recipients
              </Btn>
            </>
          )}
        </section>
      )}
    </div>
  );
}
